/**
 * Jam Sessions — Firestore CRUD + WebRTC signaling
 *
 * Collection: `jamSessions/{sessionId}`
 * Subcollection: `jamSessions/{sessionId}/participants/{participantId}`
 * Subcollection: `jamSessions/{sessionId}/signals/{signalId}` (WebRTC signaling)
 *
 * Jam sessions enable real-time audio collaboration via WebRTC peer-to-peer connections.
 * Optimised for ethernet-connected musicians for lowest latency.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

// ─── Types ──────────────────────────────────────────────────────────────────

export type JamSessionStatus = 'waiting' | 'live' | 'recording' | 'ended';

export interface JamParticipant {
  id: string;               // same as user uid
  displayName: string;
  imageUrl?: string;
  role: 'host' | 'musician';
  creativeRole?: string;     // e.g. "Producer", "Songwriter", "Engineer"
  instrument?: string;       // e.g. "Guitar", "Vocals", "Keys" (optional)
  isMuted: boolean;
  latencyMs?: number;        // measured round-trip latency
  connectionType?: string;   // 'ethernet' | 'wifi' | 'cellular'
  joinedAt: Timestamp;
}

export interface JamSession {
  id: string;
  name: string;
  code: string;             // 6-char join code
  hostId: string;
  hostName: string;
  status: JamSessionStatus;
  maxParticipants: number;
  bpm?: number;             // optional metronome BPM
  isRecording: boolean;
  recordingStartedAt?: Timestamp;
  chatRoomId?: string;      // chat room this session was started from (ensures 1 session per room)
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

const COLLECTION = 'jamSessions';

// ─── Helpers ──────────────────────────────────────────────────────────────

function generateCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no ambiguous chars
  let code = '';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

// ─── CRUD ───────────────────────────────────────────────────────────────────

/** Create a new jam session. */
export async function createJamSession(
  hostId: string,
  hostName: string,
  sessionName: string,
  maxParticipants = 8,
  chatRoomId?: string,
): Promise<JamSession> {
  const id = `jam_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const now = Timestamp.now();
  const session: JamSession = {
    id,
    name: sessionName,
    code: generateCode(),
    hostId,
    hostName,
    status: 'waiting',
    maxParticipants,
    isRecording: false,
    ...(chatRoomId ? { chatRoomId } : {}),
    createdAt: now,
    updatedAt: now,
  };
  await setDoc(doc(db, COLLECTION, id), session);
  return session;
}

/** Find the active jam session for a chat room (if any). Only 1 allowed per room. */
export async function findActiveJamSessionForRoom(chatRoomId: string): Promise<JamSession | null> {
  const q = query(
    collection(db, COLLECTION),
    where('chatRoomId', '==', chatRoomId),
    where('status', 'in', ['waiting', 'live', 'recording']),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as JamSession;
}

/** Get a single jam session by ID. */
export async function getJamSession(sessionId: string): Promise<JamSession | null> {
  const snap = await getDoc(doc(db, COLLECTION, sessionId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as JamSession;
}

/** Find a jam session by join code. */
export async function findJamSessionByCode(code: string): Promise<JamSession | null> {
  const q = query(
    collection(db, COLLECTION),
    where('code', '==', code.toUpperCase()),
    where('status', 'in', ['waiting', 'live', 'recording']),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() } as JamSession;
}

/** List active sessions for a host. */
export async function getHostSessions(hostId: string): Promise<JamSession[]> {
  const q = query(
    collection(db, COLLECTION),
    where('hostId', '==', hostId),
    where('status', 'in', ['waiting', 'live', 'recording']),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as JamSession));
}

/** Update session status. */
export async function updateJamSessionStatus(sessionId: string, status: JamSessionStatus): Promise<void> {
  await updateDoc(doc(db, COLLECTION, sessionId), {
    status,
    updatedAt: serverTimestamp(),
  });
}

/** Set BPM for the session metronome. */
export async function setJamSessionBpm(sessionId: string, bpm: number): Promise<void> {
  await updateDoc(doc(db, COLLECTION, sessionId), {
    bpm,
    updatedAt: serverTimestamp(),
  });
}

/** Toggle recording state. */
export async function toggleRecording(sessionId: string, isRecording: boolean): Promise<void> {
  const update: any = { isRecording, updatedAt: serverTimestamp() };
  if (isRecording) update.recordingStartedAt = serverTimestamp();
  await updateDoc(doc(db, COLLECTION, sessionId), update);
}

/** End session. */
export async function endJamSession(sessionId: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION, sessionId), {
    status: 'ended',
    isRecording: false,
    updatedAt: serverTimestamp(),
  });
}

// ─── Participants ──────────────────────────────────────────────────────────

/** Join a session as a participant. */
export async function joinJamSession(
  sessionId: string,
  participant: Omit<JamParticipant, 'joinedAt'>,
): Promise<void> {
  const data: Record<string, unknown> = { ...participant, joinedAt: serverTimestamp() };
  // Firestore rejects undefined values — strip them defensively
  Object.keys(data).forEach(k => data[k] === undefined && delete data[k]);
  await setDoc(doc(db, COLLECTION, sessionId, 'participants', participant.id), data);
}

/** Leave a session. */
export async function leaveJamSession(sessionId: string, participantId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, sessionId, 'participants', participantId));
}

/** Update participant mute state / latency. */
export async function updateParticipant(
  sessionId: string,
  participantId: string,
  data: Partial<JamParticipant>,
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, sessionId, 'participants', participantId), data);
}

/** Subscribe to participants list in real-time. */
export function subscribeParticipants(
  sessionId: string,
  callback: (participants: JamParticipant[]) => void,
): Unsubscribe {
  const q = collection(db, COLLECTION, sessionId, 'participants');
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as JamParticipant)));
  });
}

/** Subscribe to session changes in real-time. */
export function subscribeJamSession(
  sessionId: string,
  callback: (session: JamSession | null) => void,
): Unsubscribe {
  return onSnapshot(doc(db, COLLECTION, sessionId), (snap) => {
    if (!snap.exists()) { callback(null); return; }
    callback({ id: snap.id, ...snap.data() } as JamSession);
  });
}

// ─── WebRTC Signaling ──────────────────────────────────────────────────────

export interface SignalMessage {
  id: string;
  from: string;
  to: string;
  type: 'offer' | 'answer' | 'ice-candidate';
  payload: any;
  createdAt: Timestamp;
}

/** Send a signaling message. */
export async function sendSignal(
  sessionId: string,
  signal: Omit<SignalMessage, 'id' | 'createdAt'>,
): Promise<void> {
  const id = `sig_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  await setDoc(doc(db, COLLECTION, sessionId, 'signals', id), {
    ...signal,
    id,
    createdAt: serverTimestamp(),
  });
}

/** Subscribe to signals for a specific participant. */
export function subscribeSignals(
  sessionId: string,
  participantId: string,
  callback: (signals: SignalMessage[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION, sessionId, 'signals'),
    where('to', '==', participantId),
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as SignalMessage)));
  });
}

/** Clean up processed signals. */
export async function deleteSignal(sessionId: string, signalId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, sessionId, 'signals', signalId));
}
